import { Sentry } from '@/sentry';

/** Stable categories for Sentry Logs / breadcrumbs filtering. */
export type TelemetryCategory =
  | 'auth'
  | 'api'
  | 'sync'
  | 'recorder'
  | 'replay'
  | 'map'
  | 'native';

type TelemetryData = Record<string, unknown>;

function safeCall(fn: () => void): void {
  try {
    fn();
  } catch {
    /* telemetry must never break product flows */
  }
}

function attrs(
  category: TelemetryCategory,
  data?: TelemetryData,
): Record<string, unknown> {
  return data ? { category, ...data } : { category };
}

/** Breadcrumb only (no Log, no Issue). */
export function breadcrumb(
  category: TelemetryCategory,
  message: string,
  data?: TelemetryData,
): void {
  safeCall(() => {
    Sentry.addBreadcrumb({ category, level: 'info', message, data });
  });
}

/** Notable step: Log + breadcrumb. */
export function log(
  category: TelemetryCategory,
  message: string,
  data?: TelemetryData,
): void {
  safeCall(() => {
    Sentry.logger.info(message, attrs(category, data));
    Sentry.addBreadcrumb({ category, level: 'info', message, data });
  });
}

/** Warning: Log + breadcrumb, no Issue. */
export function warn(
  category: TelemetryCategory,
  message: string,
  data?: TelemetryData,
): void {
  safeCall(() => {
    Sentry.logger.warn(message, attrs(category, data));
    Sentry.addBreadcrumb({ category, level: 'warning', message, data });
  });
}

/** Failure: error Log + breadcrumb + Issue. */
export function captureFailure(
  category: TelemetryCategory,
  message: string,
  error?: unknown,
  data?: TelemetryData,
): void {
  safeCall(() => {
    Sentry.logger.error(message, attrs(category, data));
    Sentry.addBreadcrumb({ category, level: 'error', message, data });
    if (error instanceof Error) {
      Sentry.captureException(error, {
        tags: { domain: category },
        extra: data,
      });
    } else {
      Sentry.captureMessage(message, {
        level: 'error',
        tags: { domain: category },
        extra: { ...data, detail: error },
      });
    }
  });
}

/** Attach authenticated user id only (no email / PII). */
export function setUser(id: string): void {
  safeCall(() => {
    Sentry.setUser({ id });
  });
}

export function clearUser(): void {
  safeCall(() => {
    Sentry.setUser(null);
  });
}

/** Sync Sentry user scope from profile id (or clear when logged out). */
export function syncUser(id: string | null | undefined): void {
  if (typeof id === 'string' && id.length > 0) {
    setUser(id);
  } else {
    clearUser();
  }
}
